import { Gender, NavigationLinkMapping, UserConfig, UserConfigDefinition } from '@statsbomb/parachute-types';
import { PrimitiveAtom } from 'jotai';
import { CustomShape } from '@statsbomb/kitbag-datavis';
import { KeysToCamelCase, Option } from './generic';
import { DashboardWidgetConfigVis, ReportWidgetVis } from './widget';
import { GameRangeFilterOption, GameType } from './filters';
import { TranslatedOption } from './translations';
import { PeriodRange } from './game';
import { MetricDistributionRange } from './metric';

export type UserConfigCamelCase = KeysToCamelCase<UserConfig>;

type UserConfigWithoutDefinition = KeysToCamelCase<Omit<UserConfig, 'definition'>>;

export interface UserConfigReport {
  name?: string;
  widgets: ReportWidgetVis[];
}

/* The omitting of definition is this file is so that we can redeclare the expected
definition for a particular user config type */
export interface UserConfigReportDefinition extends UserConfigWithoutDefinition {
  definition: UserConfigReport;
}

export interface UserConfigDashboardTemplateDefinition extends UserConfigWithoutDefinition {
  definition: {
    entity: string;
    name: string;
    widgetIds: string[];
  };
}

export interface UserConfigWidgetDefinition extends UserConfigWithoutDefinition {
  definition: DashboardWidgetConfigVis;
}

export interface UserConfigFilter {
  name: string;
  filters: Partial<ConfigFilterSet>;
}

export interface UserConfigFilterDefinition extends UserConfigWithoutDefinition {
  definition: UserConfigFilter;
}

export interface UserConfigTableColumns {
  tableId: string;
  selectedColumns: string[];
}

export interface UserConfigTableColumnsDefinition extends UserConfigWithoutDefinition {
  definition: UserConfigTableColumns;
}

export interface UserConfigGenderDefinition extends UserConfigWithoutDefinition {
  definition: {
    gender: Gender;
  };
}

export interface UserConfigSettingsDefinition extends UserConfigWithoutDefinition {
  definition: {
    team: NavigationLinkMapping;
  };
}

export enum FirstTimeModalTypes {
  PLAYER_FEATURES = 'playerFeatures',
  SCOUT_FEATURES = 'scoutFeatures',
  TEAM_EXPLORE = 'teamExplore',
  EVENT_EXPLORE = 'eventExplore',
}

export interface UserConfigFirstTimeModalDefinition extends UserConfigWithoutDefinition {
  definition: Partial<Record<FirstTimeModalTypes, boolean>>;
}

export enum FilterSetType {
  FILTERS = 'filters',
  PLAYER_DATA_LOCKER = 'playerDataLockerFilters',
  TEAM_DATA_LOCKER = 'teamDataLockerFilters',
  EVENT_DATA_LOCKER = 'eventDataLockerFilters',
}

export type UserConfigType = 'dashboard' | 'gender' | 'settings' | 'tableColumns' | FilterSetType | 'firstTimeModal';

export interface UserConfigPutMutationParams {
  configId?: string;
  configType: UserConfigType;
  configDefinition: UserConfigDefinition;
}

export interface UserConfigPatchMutationParams {
  configId: string;
  configDefinition: UserConfigDefinition;
}

/**
 * Filter set types - if you add a new filter that you want to persist add it here and all the other places
 * it needs to be added will give you typescript errors
 */
type GameConfigFilterAtomValues = {
  periodRange: PeriodRange;
  gameRange: GameRangeFilterOption;
  startDate: string;
  endDate: string;
  gameType: TranslatedOption<GameType>;
  deselectedGameIds: Option[];
};

type EventConfigFilterAtomValues = {
  eventNames: string[];
  outcomes: string[];
  bodyParts: string[];
  passHeights: string[];
  keeperActions: string[];
  pitchStartZones: CustomShape[];
  pitchEndZones: CustomShape[];
};

type PlayerConfigFilterAtomValues = {
  minAge: number;
  maxAge: number;
  minHeight: number;
  maxHeight: number;
  minMinutes: number;
  maxMinutes: number;
  preferredFoot: string[];
};

export interface ConfigFilterAtomValues
  extends GameConfigFilterAtomValues,
    EventConfigFilterAtomValues,
    PlayerConfigFilterAtomValues {
  competitionIds: number[] | null;
  seasonIds: number[] | null;
  playerIds: number[];
  teamIds: number[];
  positions: string[];
  metric?: string;
  metricDistributions: MetricDistributionRange[];
  gender?: Gender;
}

export type ConfigGameFilterKey = keyof GameConfigFilterAtomValues;

type ConfigEventFilterKey = keyof EventConfigFilterAtomValues;

type ConfigPlayerFilterKey = keyof PlayerConfigFilterAtomValues;

export type ConfigFilterKey = keyof ConfigFilterAtomValues;

// As the filters property is very flexible it
// doesn't make sense to restrict it to any specific type.
// We opted to use any over unknown here because unknown does type
// checking against the atom we are trying to hydrate
// and as the value types don't crossover, typescript complains.
type FilterSetMapping<T extends ConfigFilterKey> = Record<T, any>;

export type ConfigGameFilterSet = FilterSetMapping<ConfigGameFilterKey>;

export type ConfigEventFilterSet = FilterSetMapping<ConfigEventFilterKey>;

export type ConfigPlayerFilterSet = FilterSetMapping<ConfigPlayerFilterKey>;

// Gender and metric are optional settings which are sometimes saved to the config (because they default to undefined),
// so we add them as optional here.
export type ConfigFilterSet = Omit<FilterSetMapping<ConfigFilterKey>, 'gender' | 'metric'> & {
  metric?: string;
  gender?: Gender;
};

type FilterAtomMapping<T extends ConfigFilterKey> = Record<T, PrimitiveAtom<any>>;

export type ConfigGameFilterAtoms = FilterAtomMapping<ConfigGameFilterKey>;

export type ConfigEventFilterAtoms = FilterAtomMapping<ConfigEventFilterKey>;

export type ConfigPlayerFilterAtoms = FilterAtomMapping<ConfigPlayerFilterKey>;

export type ConfigFilterAtoms = FilterAtomMapping<ConfigFilterKey>;

type AtomHydrateCallbackMapping<T extends ConfigFilterKey> = Record<T, (value: any) => any>;

export type ConfigGameFilterAtomHydrateCallbacks = AtomHydrateCallbackMapping<ConfigGameFilterKey>;

export type ConfigEventFilterAtomHydrateCallbacks = AtomHydrateCallbackMapping<ConfigEventFilterKey>;

export type ConfigPlayerFilterAtomHydrateCallbacks = AtomHydrateCallbackMapping<ConfigPlayerFilterKey>;

export type ConfigFilterAtomHydrateCallbacks = AtomHydrateCallbackMapping<ConfigFilterKey>;

/**
 * Display setting types - if you add a new display setting that you want to persist add it here and all the other places
 * it needs to be added will give you typescript errors
 */
export enum ConfigDisplaySettingKey {
  RADAR_TEMPLATE_ID = 'radarTemplateId',
  PITCH_ORIENTATION = 'pitchOrientation',
  PITCH_CROP = 'pitchCrop',
  SHOW_ARROWS = 'showArrows',
  IS_ORIGIN_PLACEMENT = 'isStartPlacement',
  PITCH_MARKER_COLOURING_MODE = 'pitchMarkerColouringMode',
  ORDER_BY = 'orderBy',
  ORDER_DIRECTION = 'orderDirection',
  BAR_CHART_ORIENTATION = 'barChartOrientation',
}

export type ConfigDisplaySettings = Record<ConfigDisplaySettingKey, any>;

export type ConfigDisplaySettingAtoms = Record<ConfigDisplaySettingKey, PrimitiveAtom<any>>;

export type ConfigDisplaySettingAtomHydrateCallbacks = Record<ConfigDisplaySettingKey, (value: any) => any>;

export type AtomsToHydrate = [PrimitiveAtom<unknown>, unknown][];
